import React from 'react';
// Helpers
import { getContentTypeText } from 'helpers/socialMediaContentTypes';
import {
  isCopyItem,
  isFileItem,
  getSeparatedDeliverableItems,
} from 'helpers/deliverables';
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverables,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_files,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import { CBDeliverableItemType } from 'api/graphql-global-types';
// Styles
import styles from './DeliverablesReview.module.scss';

type DeliverablesReviewProps = {
  deliverable:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
};

// Helper functions for V3 items
const isCopyItemV3 = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2 => {
  return item.type === CBDeliverableItemType.COPY;
};

const isFileItemV3 = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2 => {
  return item.type === CBDeliverableItemType.FILE;
};

// Helper function to get the display URL
// Backend should provide the converted file URL in file.url when videoTranscodingJob exists
const getDisplayUrl = (
  file:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_files
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2
): string | undefined => {
  // Backend should update file.url to point to converted file when videoTranscodingJob exists
  return file.url || undefined;
};

// Helper function to get the key for file type detection, preferring resultKey
const getDisplayKey = (
  file:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_files
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2
): string | null => {
  // Check if this is a V2 file item with videoTranscodingJob
  // Type will be updated after GraphQL types are regenerated
  if ('videoTranscodingJob' in file) {
    const fileItem = file as any;
    if (fileItem.videoTranscodingJob?.resultKey) {
      return fileItem.videoTranscodingJob.resultKey;
    }
  }
  return file.key || null;
};

const FilePreview = ({
  file,
}: {
  file:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_files
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2;
}) => {
  const displayKey = getDisplayKey(file);
  const displayUrl = getDisplayUrl(file);
  const isImage = displayKey?.match(/\.(jpeg|jpg|png|gif|webp)$/i);
  const isVideo = displayKey?.match(/\.(mp4|mov|avi|webm)$/i);

  return (
    <div key={displayKey || file.key} className={styles.previewWrapper}>
      <p className={styles.fileName}>{file.name}</p>
      {isImage ? (
        <img src={displayUrl} alt={file.name} className={styles.preview} />
      ) : isVideo ? (
        <video
          src={displayUrl}
          controls
          className={styles.preview}
          crossOrigin="anonymous"
        >
          <track
            kind="captions"
            label="English captions"
            srcLang="en"
            src=""
            default
          />
          <source src={displayUrl} />
          Your browser does not support the video tag.
        </video>
      ) : (
        <p>Unsupported file type</p>
      )}
    </div>
  );
};

const ItemPreview = ({
  item,
  index,
}: {
  item: GetCampaignOffers_getCampaignOffers_entities_deliverables_items;
  index: number;
}) => {
  const deliverableNumber = index + 1;
  const itemType = getContentTypeText(item.task);
  const itemName = item.task?.title || 'Untitled Task';
  const description = item.task?.description || '';

  return (
    <div key={item.id} className={styles.v2DeliverableItem}>
      <div>
        <strong>
          Deliverable {deliverableNumber} - {itemName}
        </strong>
      </div>
      {description && <div>Type: {itemType}</div>}
      {description && <div>Description: {description}</div>}
      <div>
        {isFileItem(item) && item.key && item.url ? (
          <FilePreview file={item} />
        ) : isCopyItem(item) ? (
          <div className={styles.itemContent}>
            <p>Post Copy:</p>
            <div className={styles.copyContent}>
              <p
                dangerouslySetInnerHTML={{
                  __html: parseForInnerHTMLWithBreaks(item.copy),
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ItemPreviewV3 = ({
  item,
  index,
}: {
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items;
  index: number;
}) => {
  const deliverableNumber = index + 1;
  const itemType = getContentTypeText(item.task);
  const itemName = item.task?.title || 'Untitled Task';
  const description = item.task?.description || '';

  return (
    <div key={item.id} className={styles.v2DeliverableItem}>
      <div>
        <strong>
          Deliverable {deliverableNumber} - {itemName}
        </strong>
      </div>
      {description && <div>Type: {itemType}</div>}
      {description && <div>Description: {description}</div>}
      <div>
        {isFileItemV3(item) && item.key && item.url ? (
          <FilePreview file={item} />
        ) : isCopyItemV3(item) ? (
          <div className={styles.itemContent}>
            <p>Post Copy:</p>
            <div className={styles.copyContent}>
              <p
                dangerouslySetInnerHTML={{
                  __html: parseForInnerHTMLWithBreaks(item.copy),
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// New component for V3 task-based deliverables
const V3TaskPreview = ({
  deliverable,
}: {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
}) => {
  const { files, copies } = getSeparatedDeliverableItems(deliverable);
  const taskName = deliverable.task?.title || 'Untitled Task';
  const taskDescription = deliverable.task?.description || '';
  const taskType = deliverable.task?.type;

  return (
    <div className={styles.v3TaskPreview}>
      <div className={styles.taskHeader}>
        <h3 className={styles.taskTitle}>{taskName}</h3>
        {taskDescription && (
          <p className={styles.taskDescription}>{taskDescription}</p>
        )}
        {taskType && (
          <p className={styles.taskType}>
            Type: {getContentTypeText(deliverable.task)}
          </p>
        )}
      </div>

      {/* Show content items (files) */}
      {files.length > 0 && (
        <div className={styles.contentSection}>
          <h4 className={styles.sectionTitle}>Content Items:</h4>
          <div className={styles.filesGrid}>
            {files.map((fileItem, index) => (
              <div key={fileItem.id} className={styles.contentItem}>
                <h5 className={styles.contentItemTitle}>
                  {fileItem.task?.title || `Content ${index + 1}`}
                </h5>
                {fileItem.task?.description && (
                  <p className={styles.contentItemDescription}>
                    {fileItem.task.description}
                  </p>
                )}
                {fileItem.key && fileItem.url && (
                  <FilePreview file={fileItem} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show copy items */}
      {copies.length > 0 && (
        <div className={styles.copySection}>
          <h4 className={styles.sectionTitle}>Post Copy:</h4>
          {copies.map((copyItem, index) => (
            <div key={copyItem.id} className={styles.copyItem}>
              <h5 className={styles.copyItemTitle}>
                {copyItem.task?.title || `Copy ${index + 1}`}
              </h5>
              {copyItem.task?.description && (
                <p className={styles.copyItemDescription}>
                  {copyItem.task.description}
                </p>
              )}
              <div className={styles.copyContent}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: parseForInnerHTMLWithBreaks(copyItem.copy),
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DeliverablesReview: React.FC<DeliverablesReviewProps> = ({
  deliverable,
}) => {
  // Check if this is a V3 deliverable with task structure
  const isV3Deliverable = 'task' in deliverable;

  // V3 deliverable with task structure (new V3 campaigns)
  if (
    isV3Deliverable &&
    (deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2)
      .task
  ) {
    return (
      <V3TaskPreview
        deliverable={
          deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
        }
      />
    );
  }

  // V3 deliverable (has task property but no task data - fallback to items)
  if (isV3Deliverable && 'items' in deliverable) {
    return (
      <>
        {deliverable.items && deliverable.items.length > 0 && (
          <div className={styles.v2DeliverablesList}>
            <p className={styles.title}>Deliverables Checklist:</p>
            {(
              deliverable.items as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items[]
            ).map((item, index) => (
              <ItemPreviewV3 key={item.id} item={item} index={index} />
            ))}
          </div>
        )}
      </>
    );
  }

  // V2 deliverable (no task property, but has items)
  if (
    !isV3Deliverable &&
    'items' in deliverable &&
    deliverable.items &&
    deliverable.items.length > 0
  ) {
    return (
      <>
        <div className={styles.v2DeliverablesList}>
          <p className={styles.title}>Deliverables Checklist:</p>
          {(
            deliverable.items as GetCampaignOffers_getCampaignOffers_entities_deliverables_items[]
          ).map((item, index) => (
            <ItemPreview key={item.id} item={item} index={index} />
          ))}
        </div>
      </>
    );
  }

  // V1 deliverable (no task property, no items or empty items, only files)
  if (!isV3Deliverable && 'files' in deliverable) {
    return (
      <>
        {deliverable.files && deliverable.files.length > 0 && (
          <div className={styles.filesGrid}>
            {deliverable.files.map((file, fileIndex) => (
              <FilePreview key={file.key + '_' + fileIndex} file={file} />
            ))}
          </div>
        )}
      </>
    );
  }

  // Fallback
  return null;
};

export default DeliverablesReview;
